// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import countryFeatures from '@shared/ui-kit/assets/data/country-features.json';
import { getTextBeforeSymbol, formatToPercent } from '@shared/ui-kit/utils';
import { ResponsiveChoropleth } from '@nivo/geo';
import { ChartContainer } from '@shared/ui-kit/components';
import { type FC, useMemo } from 'react';
import {
  interpolateColorOpacity,
  transformCountriesData,
} from './utils/transform-countries-data';
import { useTranslation } from 'react-i18next';

interface CountryChartProps {
  countries: string[];
  percentages: number[];
}

export const CountryChart: FC<CountryChartProps> = ({ countries, percentages }) => {
  const { t } = useTranslation();
  const data = useMemo(
    () => transformCountriesData(countries, percentages),
    [countries, percentages],
  );

  const highestPercentage = Math.max(...percentages);

  const topCountries = data?.slice(0, 10);

  const chartColors = useMemo(
    () =>
      percentages
        .map(value => interpolateColorOpacity(value, highestPercentage))
        .reverse(),
    [highestPercentage, percentages],
  );
  const numRowsForGrid = Math.max(1, Math.ceil((topCountries?.length || 0) / 2));

  return (
    <ChartContainer header={t('common.country', { ns: 'common' })}>
      <div className="h-72">
        <ResponsiveChoropleth
          data={data}
          features={countryFeatures}
          margin={{ top: 15, right: 0, bottom: 0, left: 0 }}
          colors={chartColors}
          domain={[0, highestPercentage]}
          unknownColor="#EDF0F9"
          label="properties.name"
          valueFormat=" >-.2%"
          projectionScale={78}
          projectionTranslation={[0.5, 0.5]}
          projectionRotation={[0, 0, 0]}
          graticuleLineColor="#dddddd"
          borderWidth={0.5}
          borderColor="#152538"
        />
      </div>
      <div
        className={`grid grid-rows-${numRowsForGrid} mt-6 grid-flow-col grid-cols-2 gap-x-4`}
      >
        {topCountries?.map(({ country, value }, index) => (
          <div key={index}>
            <div className="grid w-full grid-cols-[1fr_60px] border-b border-border-sep py-1">
              <div className="grid grid-cols-[4px_1fr] gap-2">
                <div className={`countries-top-ten-grid-${index}`} />
                <p className="line-clamp-1 text-primary-medium">
                  {getTextBeforeSymbol(country)}
                </p>
              </div>
              <span className="!money-display text-end text-primary-medium">
                {formatToPercent(value)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
};
